// Core
import { AllowedComponentProps, Component, VNodeProps } from "vue";

// Libraries
import { TYPE, useToast } from "vue-toastification";

// Services & Helpers
import { errorHandlerService } from "@/core/services/error";
import { sentryService } from "@/core/services/sentry/sentryService";

// Components
import ToastItem from "@/components/toast/ToastItem.vue";

// Types
import { ToastOptions } from "vue-toastification/dist/types/types";

type ComponentProps<C extends Component> = C extends new (...args: unknown[]) => unknown
  ? Omit<InstanceType<C>["$props"], keyof VNodeProps | keyof AllowedComponentProps>
  : never;

type SentryErrorContextT = {
  errorInstance?: Error;
  errorCode?: string;
  params?: Record<string, string>;
};

type CustomToastOptions<T extends Component> = ComponentProps<T> & {
  type?: TYPE;
  title?: string;
  message?: string;
  captureError?: boolean;
  errorContext?: SentryErrorContextT;
};

export const useCustomToast = <T extends Component>(component: T) => {
  const _toast = useToast();

  if (!(component as { props: object })?.props.hasOwnProperty("type")) {
    errorHandlerService.handleError("O componente passado para useCustomToast deve ter uma propriedade 'type'.");
  }

  const trigger = (options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    const { type, captureError, errorContext, ...componentSpecificProps } = options;

    _toast(
      {
        component,
        props: { ...componentSpecificProps, type }
      },
      pluginOptions
    );
  };

  const showToast = (type: TYPE, options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    trigger(
      {
        ...options,
        type
      },
      pluginOptions
    );
  };

  const success = (options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    showToast(TYPE.SUCCESS, options, pluginOptions);
  };

  const info = (options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    showToast(TYPE.INFO, options, pluginOptions);
  };

  const warning = (options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    showToast(TYPE.WARNING, options, pluginOptions);
  };

  const error = (options: CustomToastOptions<T>, pluginOptions?: ToastOptions) => {
    showToast(TYPE.ERROR, options, pluginOptions);

    const shouldCapture = options.captureError !== false;

    if (shouldCapture) {
      const errorTitle = options.title || (options as unknown as { props: { title: string } }).props?.title;
      const errorMessage = options.message || (options as unknown as { props: { message: string } }).props?.message;

      const errorToSend =
        options.errorContext?.errorInstance || new Error(errorMessage || "Erro de toast desconhecido");

      sentryService.captureError(
        errorToSend,
        options.errorContext?.errorCode,
        { title: errorTitle, message: errorMessage },
        options.errorContext?.params
      );
    }
  };

  return {
    showToast,
    success,
    info,
    warning,
    error
  };
};

// NOTE: O componente passado como parâmetro do composable **deve ter a propriedade type**.
//       O componente a ser renderizado será o passado como parâmetro, seus props serão passados para
//       options de cada método do composable.
export const toast = useCustomToast(ToastItem);
// export const progressProjectToast = useCustomToast(ProgressToast);
